import { useEffect, useMemo, useState } from 'react';

import { useTracking } from '@change-corgi/core-react/tracking';

import { usePetitionDetailsPageData } from 'src/app/pages/petitionDetails/pageContext';
import type { SupporterVoicesVideos } from 'src/app/pages/petitionDetails/pageContext';
import { useCanPlayWebm } from 'src/app/shared/hooks/causes';

import { useCampaignActionsEnabled } from '../useCampaignActionsEnabled';

import type { SupporterVideosList } from './types';

type Result = ModelHookResult<
	{
		supporterVideos: SupporterVideosList | [];
		currentlyPlaying: string;
		showCreateVideo: boolean;
	},
	{
		setCurrentlyPlaying: (videoUrl: string) => void;
		removeVideoFromState: (videoId?: string) => void;
	}
>;

export function useSupporterVideos({
	supporterVoicesVideos: { videos, enabled: supporterVoicesEnabled },
}: {
	supporterVoicesVideos: SupporterVoicesVideos;
}): Result {
	const [currentlyPlaying, setCurrentlyPlaying] = useState('');

	const {
		data: {
			petition: {
				id: petitionId,
				videoSettingsMetadata: { disableCreation: disableVideoCreation },
			},
		},
	} = usePetitionDetailsPageData();

	const canPlayWebm = useCanPlayWebm();
	const track = useTracking();
	const campaignActionsEnabled = useCampaignActionsEnabled();

	const displayVideos = useMemo(() => {
		if (canPlayWebm) return videos;
		// Right now GQL returns all transcoded + unprocessed videos. We want to display transcoded videos for browsers (e.g., iOS) that don't support .webm.
		// Mux URLs are always included because MuxPlayer handles format selection internally.
		return videos.filter(
			({ videoUrl }) => videoUrl.endsWith('.mp4') || /^https?:\/\/stream\.mux\.com\//.test(videoUrl),
		);
	}, [canPlayWebm, videos]);

	const [supporterVideos, setSupporterVideos] = useState<SupporterVideosList>(displayVideos);

	useEffect(() => {
		if (displayVideos.length > 0) {
			void track('petition_page_supporters_voice_view', {
				petition_id: petitionId,
				showSupporterVoices: supporterVoicesEnabled,
			});
		} // Making this dependent on petitionId because a bug was experienced on demo/staging where you
		// could switch to another petition and have videos from a previous page showing up.
		// This enforces that we re-call this logic anytime we switch petition pages.
	}, [petitionId]);

	const removeVideoFromState = (videoId?: string) => {
		const remainingVideos = supporterVideos.filter((v) => v.id !== videoId);
		setSupporterVideos(remainingVideos);
	};

	return {
		data: {
			supporterVideos,
			currentlyPlaying,
			showCreateVideo: campaignActionsEnabled && !disableVideoCreation,
		},
		actions: {
			setCurrentlyPlaying,
			removeVideoFromState,
		},
	};
}
